import type * as React from 'react'
import { Controller } from 'react-hook-form'
import type {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { CustomerListItem } from '#/shared/api/generated/shipment/models'
import { formatUsPhone, usPhoneDigits } from '#/shared/lib/format'
import {
  MANAGE_FIELD_KEY,
  ManageFieldsProvider,
  useCompactView,
  useFieldsVisible,
} from '#/shared/lib/manage-fields'
import { cn } from '#/shared/lib/utils'
import { ComboboxBadgeSuffix } from '#/shared/ui/combobox'
import { FormCard } from '#/shared/ui/form-card'
import { ChevronDownIcon } from '#/shared/ui/icons'
import { Textarea } from '#/shared/ui/textarea'

import type { CustomerFormData } from '../model/customer-schema'
import { CustomerNameCombobox } from './customer-name-combobox'
import { CustomerSearchCombobox } from './customer-search-combobox'

export type EmailCheckStatus = 'idle' | 'checking' | 'available' | 'taken'

export type CustomerSavedField =
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'mobile'
  | 'companyPhone'
  | 'email'
  | 'email2'
  | 'billingEmail'
  | 'company'

interface CustomerCardProps {
  control: Control<CustomerFormData>
  register: UseFormRegister<CustomerFormData>
  setValue?: UseFormSetValue<CustomerFormData>
  errors: FieldErrors<CustomerFormData>
  bare?: boolean
  searchPlaceholders?: boolean
  onCustomerSelect?: (customer: CustomerListItem) => void
  emailStatus?: EmailCheckStatus
  customerSavedField?: CustomerSavedField | null
  onSaveAsNewCustomer?: (field: CustomerSavedField) => void
  onClearNewCustomer?: () => void
}

function useSaveAsCustomerAction(
  customerSavedField: CustomerSavedField | null | undefined,
  field: CustomerSavedField,
  onSaveAsNewCustomer: ((field: CustomerSavedField) => void) | undefined,
) {
  const { t } = useTranslation()
  if (customerSavedField != null || !onSaveAsNewCustomer) return undefined
  return {
    label: t('orderCreate.customer.saveAsNewCustomer'),
    onClick: () => onSaveAsNewCustomer(field),
  }
}

function useSavedBadge(
  customerSavedField: CustomerSavedField | null | undefined,
  field: CustomerSavedField,
  onClearNewCustomer: (() => void) | undefined,
) {
  const { t } = useTranslation()
  if (customerSavedField !== field) return undefined
  return (
    <ComboboxBadgeSuffix
      label={t('orderCreate.customer.newCustomerBadge')}
      onClick={onClearNewCustomer}
    />
  )
}

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div className="flex items-start gap-2.5">{children}</div>
}

function FullRow({ children }: { children: React.ReactNode }) {
  return <div className="min-w-0 flex-1">{children}</div>
}

function HalfCell({ children }: { children: React.ReactNode }) {
  return <div className="min-w-0 flex-1">{children}</div>
}

type FormProps = Omit<CustomerCardProps, 'bare'>

const CUSTOMER_TO_FORM_MAP: ReadonlyArray<
  [keyof CustomerFormData, keyof CustomerListItem]
> = [
  ['firstName', 'first_name'],
  ['lastName', 'last_name'],
  ['company', 'company'],
  ['billingEmail', 'billing_email'],
  ['email', 'email'],
  ['email2', 'secondary_email'],
  ['note', 'note'],
]

const CUSTOMER_PHONE_MAP: ReadonlyArray<
  [keyof CustomerFormData, keyof CustomerListItem]
> = [
  ['phone', 'phone'],
  ['mobile', 'secondary_phone'],
  ['companyPhone', 'company_phone'],
]

function applyCustomerToForm(
  customer: CustomerListItem,
  setValue: UseFormSetValue<CustomerFormData>,
) {
  const setOpts = { shouldValidate: true, shouldDirty: true } as const
  for (const [target, source] of CUSTOMER_TO_FORM_MAP) {
    const value = customer[source]
    if (typeof value === 'string') setValue(target, value, setOpts)
  }
  for (const [target, source] of CUSTOMER_PHONE_MAP) {
    const value = customer[source]
    if (typeof value === 'string')
      setValue(target, usPhoneDigits(value), setOpts)
  }
}

function phoneTag(value: string | null | undefined): string | undefined {
  const formatted = formatUsPhone(value)
  return formatted.length > 0 ? formatted : undefined
}

function emailTag(value: string | null | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined
}

function companyOptionLabel(customer: CustomerListItem): string {
  return customer.company && customer.company.length > 0
    ? customer.company
    : '—'
}

function customerNameTag(customer: CustomerListItem): string | undefined {
  const name = [customer.first_name, customer.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return name.length > 0 ? name : undefined
}

function createCustomerSelect(
  setValue: UseFormSetValue<CustomerFormData> | undefined,
  onCustomerSelect?: (customer: CustomerListItem) => void,
) {
  return (customer: CustomerListItem) => {
    if (setValue) applyCustomerToForm(customer, setValue)
    onCustomerSelect?.(customer)
  }
}

function CustomerPhoneSearchCell({
  name,
  labelKey,
  tagSource,
  control,
  setValue,
  errors,
  onCustomerSelect,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
}: {
  name: 'phone' | 'mobile' | 'companyPhone'
  labelKey: string
  tagSource: 'phone' | 'secondary_phone' | 'company_phone'
  control: Control<CustomerFormData>
  setValue?: UseFormSetValue<CustomerFormData>
  errors: FieldErrors<CustomerFormData>
  onCustomerSelect?: (customer: CustomerListItem) => void
  customerSavedField?: CustomerSavedField | null
  onSaveAsNewCustomer?: (field: CustomerSavedField) => void
  onClearNewCustomer?: () => void
}) {
  const { t } = useTranslation()
  const handleSelect = createCustomerSelect(setValue, onCustomerSelect)
  const action = useSaveAsCustomerAction(
    customerSavedField,
    name,
    onSaveAsNewCustomer,
  )
  const badge = useSavedBadge(customerSavedField, name, onClearNewCustomer)
  const isSavedHere = customerSavedField === name
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <CustomerSearchCombobox
          phone
          label={t(labelKey)}
          value={field.value}
          onValueChange={field.onChange}
          onCustomerSelect={handleSelect}
          getTag={(customer) => phoneTag(customer[tagSource])}
          error={!!errors[name]}
          searchDisabled={customerSavedField != null}
          action={action}
          suffix={badge}
          onClear={isSavedHere ? onClearNewCustomer : undefined}
        />
      )}
    />
  )
}

function CustomerEmailSearchCell({
  name,
  labelKey,
  placeholderKey,
  tagSource,
  required,
  control,
  setValue,
  errors,
  onCustomerSelect,
  emailStatus,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
}: {
  name: 'email' | 'email2' | 'billingEmail'
  labelKey: string
  placeholderKey?: string
  tagSource: 'email' | 'secondary_email' | 'billing_email'
  required?: boolean
  control: Control<CustomerFormData>
  setValue?: UseFormSetValue<CustomerFormData>
  errors: FieldErrors<CustomerFormData>
  onCustomerSelect?: (customer: CustomerListItem) => void
  emailStatus?: EmailCheckStatus
  customerSavedField?: CustomerSavedField | null
  onSaveAsNewCustomer?: (field: CustomerSavedField) => void
  onClearNewCustomer?: () => void
}) {
  const { t } = useTranslation()
  const handleSelect = createCustomerSelect(setValue, onCustomerSelect)
  const status = emailStatus ?? 'idle'
  const label = required ? `${t(labelKey)} *` : t(labelKey)
  const action = useSaveAsCustomerAction(
    customerSavedField,
    name,
    onSaveAsNewCustomer,
  )
  const badge = useSavedBadge(customerSavedField, name, onClearNewCustomer)
  const isSavedHere = customerSavedField === name
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <CustomerSearchCombobox
          label={label}
          placeholder={placeholderKey ? t(placeholderKey) : undefined}
          value={field.value}
          onValueChange={field.onChange}
          onCustomerSelect={handleSelect}
          getTag={(customer) => emailTag(customer[tagSource])}
          error={!!errors[name] || status === 'taken'}
          success={required === true && status === 'available'}
          searchDisabled={customerSavedField != null}
          action={action}
          suffix={badge}
          onClear={isSavedHere ? onClearNewCustomer : undefined}
        />
      )}
    />
  )
}

function CustomerCompanySearchCell({
  control,
  setValue,
  errors,
  onCustomerSelect,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
}: {
  control: Control<CustomerFormData>
  setValue?: UseFormSetValue<CustomerFormData>
  errors: FieldErrors<CustomerFormData>
  onCustomerSelect?: (customer: CustomerListItem) => void
  customerSavedField?: CustomerSavedField | null
  onSaveAsNewCustomer?: (field: CustomerSavedField) => void
  onClearNewCustomer?: () => void
}) {
  const { t } = useTranslation()
  const handleSelect = createCustomerSelect(setValue, onCustomerSelect)
  const action = useSaveAsCustomerAction(
    customerSavedField,
    'company',
    onSaveAsNewCustomer,
  )
  const badge = useSavedBadge(customerSavedField, 'company', onClearNewCustomer)
  const isSavedHere = customerSavedField === 'company'
  return (
    <Controller
      control={control}
      name="company"
      render={({ field }) => (
        <CustomerSearchCombobox
          label={t('orderCreate.customer.company')}
          value={field.value}
          onValueChange={field.onChange}
          onCustomerSelect={handleSelect}
          getLabel={companyOptionLabel}
          getTag={customerNameTag}
          error={!!errors.company}
          searchDisabled={customerSavedField != null}
          action={action}
          suffix={badge}
          onClear={isSavedHere ? onClearNewCustomer : undefined}
        />
      )}
    />
  )
}

interface CompanySectionProps extends FormProps {
  showCompany: boolean
  showCompanyPhone: boolean
  showBillingEmail: boolean
}

function CompanySection({
  control,
  setValue,
  errors,
  onCustomerSelect,
  showCompany,
  showCompanyPhone,
  showBillingEmail,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
}: CompanySectionProps) {
  return (
    <div className="flex flex-col gap-2.5">
      {(showCompany || showCompanyPhone) && (
        <FieldRow>
          {showCompany && (
            <HalfCell>
              <CustomerCompanySearchCell
                control={control}
                setValue={setValue}
                errors={errors}
                onCustomerSelect={onCustomerSelect}
                customerSavedField={customerSavedField}
                onSaveAsNewCustomer={onSaveAsNewCustomer}
                onClearNewCustomer={onClearNewCustomer}
              />
            </HalfCell>
          )}
          {showCompanyPhone && (
            <HalfCell>
              <CustomerPhoneSearchCell
                name="companyPhone"
                labelKey="orderCreate.customer.companyPhone"
                tagSource="company_phone"
                control={control}
                setValue={setValue}
                errors={errors}
                onCustomerSelect={onCustomerSelect}
                customerSavedField={customerSavedField}
                onSaveAsNewCustomer={onSaveAsNewCustomer}
                onClearNewCustomer={onClearNewCustomer}
              />
            </HalfCell>
          )}
        </FieldRow>
      )}
      {showBillingEmail && (
        <FieldRow>
          <FullRow>
            <CustomerEmailSearchCell
              name="billingEmail"
              labelKey="orderCreate.customer.billingEmail"
              tagSource="billing_email"
              control={control}
              setValue={setValue}
              errors={errors}
              onCustomerSelect={onCustomerSelect}
              customerSavedField={customerSavedField}
              onSaveAsNewCustomer={onSaveAsNewCustomer}
              onClearNewCustomer={onClearNewCustomer}
            />
          </FullRow>
        </FieldRow>
      )}
    </div>
  )
}

function NameRow({
  control,
  setValue,
  errors,
  onCustomerSelect,
  searchPlaceholders,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
}: FormProps & { onCustomerSelect?: (customer: CustomerListItem) => void }) {
  const { t } = useTranslation()
  const handleSelect = (customer: CustomerListItem) => {
    if (setValue) applyCustomerToForm(customer, setValue)
    onCustomerSelect?.(customer)
  }
  const firstAction = useSaveAsCustomerAction(
    customerSavedField,
    'firstName',
    onSaveAsNewCustomer,
  )
  const lastAction = useSaveAsCustomerAction(
    customerSavedField,
    'lastName',
    onSaveAsNewCustomer,
  )
  const firstBadge = useSavedBadge(
    customerSavedField,
    'firstName',
    onClearNewCustomer,
  )
  const lastBadge = useSavedBadge(
    customerSavedField,
    'lastName',
    onClearNewCustomer,
  )
  const isFirstSaved = customerSavedField === 'firstName'
  const isLastSaved = customerSavedField === 'lastName'
  return (
    <FieldRow>
      <HalfCell>
        <Controller
          control={control}
          name="firstName"
          render={({ field }) => (
            <CustomerNameCombobox
              label={t('orderCreate.customer.firstName')}
              placeholder={
                searchPlaceholders
                  ? t('orderCreate.customer.placeholders.searchName')
                  : undefined
              }
              value={field.value}
              onValueChange={field.onChange}
              onCustomerSelect={handleSelect}
              error={!!errors.firstName}
              searchDisabled={customerSavedField != null}
              action={firstAction}
              suffix={firstBadge}
              onClear={isFirstSaved ? onClearNewCustomer : undefined}
            />
          )}
        />
      </HalfCell>
      <HalfCell>
        <Controller
          control={control}
          name="lastName"
          render={({ field }) => (
            <CustomerNameCombobox
              label={t('orderCreate.customer.lastName')}
              value={field.value}
              onValueChange={field.onChange}
              onCustomerSelect={handleSelect}
              error={!!errors.lastName}
              searchDisabled={customerSavedField != null}
              action={lastAction}
              suffix={lastBadge}
              onClear={isLastSaved ? onClearNewCustomer : undefined}
            />
          )}
        />
      </HalfCell>
    </FieldRow>
  )
}

function PrimaryContactRow({
  control,
  setValue,
  errors,
  onCustomerSelect,
  emailStatus,
  searchPlaceholders,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
}: FormProps) {
  return (
    <FieldRow>
      <HalfCell>
        <CustomerPhoneSearchCell
          name="phone"
          labelKey="orderCreate.customer.phone"
          tagSource="phone"
          control={control}
          setValue={setValue}
          errors={errors}
          onCustomerSelect={onCustomerSelect}
          customerSavedField={customerSavedField}
          onSaveAsNewCustomer={onSaveAsNewCustomer}
          onClearNewCustomer={onClearNewCustomer}
        />
      </HalfCell>
      <HalfCell>
        <CustomerEmailSearchCell
          name="email"
          required
          labelKey="orderCreate.customer.email"
          placeholderKey={
            searchPlaceholders
              ? 'orderCreate.customer.placeholders.searchEmail'
              : undefined
          }
          tagSource="email"
          control={control}
          setValue={setValue}
          errors={errors}
          onCustomerSelect={onCustomerSelect}
          emailStatus={emailStatus}
          customerSavedField={customerSavedField}
          onSaveAsNewCustomer={onSaveAsNewCustomer}
          onClearNewCustomer={onClearNewCustomer}
        />
      </HalfCell>
    </FieldRow>
  )
}

interface AlternativeContactRowProps extends FormProps {
  showAltPhone: boolean
  showAltEmail: boolean
}

function AlternativeContactRow({
  control,
  setValue,
  errors,
  onCustomerSelect,
  showAltPhone,
  showAltEmail,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
}: AlternativeContactRowProps) {
  return (
    <FieldRow>
      {showAltPhone && (
        <HalfCell>
          <CustomerPhoneSearchCell
            name="mobile"
            labelKey="orderCreate.customer.mobile"
            tagSource="secondary_phone"
            control={control}
            setValue={setValue}
            errors={errors}
            onCustomerSelect={onCustomerSelect}
            customerSavedField={customerSavedField}
            onSaveAsNewCustomer={onSaveAsNewCustomer}
            onClearNewCustomer={onClearNewCustomer}
          />
        </HalfCell>
      )}
      {showAltEmail && (
        <HalfCell>
          <CustomerEmailSearchCell
            name="email2"
            labelKey="orderCreate.customer.email2"
            tagSource="secondary_email"
            control={control}
            setValue={setValue}
            errors={errors}
            onCustomerSelect={onCustomerSelect}
            customerSavedField={customerSavedField}
            onSaveAsNewCustomer={onSaveAsNewCustomer}
            onClearNewCustomer={onClearNewCustomer}
          />
        </HalfCell>
      )}
    </FieldRow>
  )
}

function NoteSection({ register }: FormProps) {
  const { t } = useTranslation()
  return (
    <FieldRow>
      <FullRow>
        <Textarea
          label={t('orderCreate.customer.note')}
          labelSize="sm"
          resize="vertical"
          className="min-h-28"
          {...register('note')}
        />
      </FullRow>
    </FieldRow>
  )
}

function ExpandToggle({
  expanded,
  onToggle,
}: {
  expanded: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex cursor-pointer items-center justify-center gap-2.5 self-center text-sm text-gray-500 transition-colors hover:text-gray-900"
      aria-expanded={expanded}
    >
      <span>
        {expanded
          ? t('orderCreate.customer.showLess')
          : t('orderCreate.customer.moreDetails')}
      </span>
      <ChevronDownIcon
        className={cn(
          'size-2.5 shrink-0 transition-transform duration-200',
          expanded && 'rotate-180',
        )}
      />
    </button>
  )
}

function Collapsible({
  expanded,
  children,
}: {
  expanded: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        '-mx-1 grid transition-[grid-template-rows] duration-200 ease-out',
        expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
      aria-hidden={!expanded}
    >
      <div className="overflow-hidden px-1">{children}</div>
    </div>
  )
}

const CUSTOMER_FIELD_KEYS = [
  MANAGE_FIELD_KEY.customerCompany,
  MANAGE_FIELD_KEY.customerCompanyPhone,
  MANAGE_FIELD_KEY.customerBillingEmail,
  MANAGE_FIELD_KEY.customerAltPhone,
  MANAGE_FIELD_KEY.customerAltEmail,
  MANAGE_FIELD_KEY.customerNote,
] as const

function useCustomerVisibility() {
  const v = useFieldsVisible(CUSTOMER_FIELD_KEYS)
  return {
    showCompany: v[MANAGE_FIELD_KEY.customerCompany],
    showCompanyPhone: v[MANAGE_FIELD_KEY.customerCompanyPhone],
    showBillingEmail: v[MANAGE_FIELD_KEY.customerBillingEmail],
    showAltPhone: v[MANAGE_FIELD_KEY.customerAltPhone],
    showAltEmail: v[MANAGE_FIELD_KEY.customerAltEmail],
    showNote: v[MANAGE_FIELD_KEY.customerNote],
    hasCompanyRow:
      v[MANAGE_FIELD_KEY.customerCompany] ||
      v[MANAGE_FIELD_KEY.customerCompanyPhone] ||
      v[MANAGE_FIELD_KEY.customerBillingEmail],
    hasAltRow:
      v[MANAGE_FIELD_KEY.customerAltPhone] ||
      v[MANAGE_FIELD_KEY.customerAltEmail],
  }
}

function CustomerFields({
  onCustomerSelect,
  ...form
}: FormProps & {
  onCustomerSelect?: (customer: CustomerListItem) => void
}) {
  const { enabled, compact, toggle } = useCompactView()
  const expanded = !compact
  const vis = useCustomerVisibility()
  return (
    <div className="flex flex-col">
      {vis.hasCompanyRow && (
        <Collapsible expanded={expanded}>
          <div className="pb-7.5">
            <CompanySection
              {...form}
              onCustomerSelect={onCustomerSelect}
              showCompany={vis.showCompany}
              showCompanyPhone={vis.showCompanyPhone}
              showBillingEmail={vis.showBillingEmail}
            />
          </div>
        </Collapsible>
      )}
      <div className="flex flex-col gap-1.75">
        <NameRow {...form} onCustomerSelect={onCustomerSelect} />
        <PrimaryContactRow {...form} onCustomerSelect={onCustomerSelect} />
      </div>
      {vis.hasAltRow && (
        <Collapsible expanded={expanded}>
          <div className="pt-1.75 pb-1">
            <AlternativeContactRow
              {...form}
              onCustomerSelect={onCustomerSelect}
              showAltPhone={vis.showAltPhone}
              showAltEmail={vis.showAltEmail}
            />
          </div>
        </Collapsible>
      )}
      {vis.showNote && (
        <Collapsible expanded={expanded}>
          <div className="pt-7.5">
            <NoteSection {...form} />
          </div>
        </Collapsible>
      )}
      {enabled && (
        <div className="flex justify-center pt-5">
          <ExpandToggle expanded={expanded} onToggle={toggle} />
        </div>
      )}
    </div>
  )
}

export function CustomerCard({
  control,
  register,
  setValue,
  errors,
  bare = false,
  searchPlaceholders = false,
  onCustomerSelect,
  emailStatus,
  customerSavedField,
  onSaveAsNewCustomer,
  onClearNewCustomer,
}: CustomerCardProps) {
  const { t } = useTranslation()
  // Activate the manage-fields context here so the customize-fields/compact-view
  // preferences (stored globally) apply everywhere the card is used — create
  // pages, the sidebar editor, the detail-page editor and the create dialog —
  // not just under the order form's provider.
  const fields = (
    <ManageFieldsProvider>
      <CustomerFields
        control={control}
        register={register}
        setValue={setValue}
        errors={errors}
        emailStatus={emailStatus}
        searchPlaceholders={searchPlaceholders}
        onCustomerSelect={onCustomerSelect}
        customerSavedField={customerSavedField}
        onSaveAsNewCustomer={onSaveAsNewCustomer}
        onClearNewCustomer={onClearNewCustomer}
      />
    </ManageFieldsProvider>
  )
  if (bare) {
    return <div className="flex w-full flex-col px-2.5 pb-2.5">{fields}</div>
  }
  return (
    <FormCard title={t('orderCreate.customer.title')} className="pb-5">
      {fields}
    </FormCard>
  )
}
